import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AlertCircle, Loader2, Minus, UserPlus, Users } from 'lucide-react';
import companiesService from '@/services/companies/companiesService';
import userService from '@/services/setting/userService';

const formatCurrency = (value, currency = 'USD') =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value) || 0);

const formatDate = (value) => {
  if (!value) return null;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? null
    : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * Seats on the subscription. A seat is a person, so the count is not something
 * you type — it moves when someone is added or removed.
 */
const SeatsCard = () => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOccupants, setShowOccupants] = useState(false);
  const [removingUuid, setRemovingUuid] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      setLoading(true);
      const { status, data } = await companiesService.getSeatSummary();

      if (status) {
        setSummary(data || null);
      }
    } catch (error) {
      toast.error('Could not load seats.', {
        description: error?.message || 'Please try again later.',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleRemoveSeat = async (occupant) => {
    const credit = Number(occupant.credit_if_removed) || 0;
    const confirmed = window.confirm(
      `Remove ${occupant.name || occupant.email} from the subscription?\n\n` +
      `They will be disabled and can no longer sign in. ` +
      `${formatCurrency(credit)} of unused time will be credited against your next renewal.`
    );

    if (!confirmed) {
      return;
    }

    setRemovingUuid(occupant.user_uuid);

    try {
      const { status, message } = await userService.DeactivateUser(occupant.user_uuid);

      if (status) {
        toast.success('Seat removed.', {
          description: `${formatCurrency(credit)} credited towards your next renewal.`,
        });
        await loadSummary();
      } else {
        toast.error('Could not remove the seat.', { description: message || 'Please try again.' });
      }
    } catch (error) {
      toast.error('Could not remove the seat.', {
        description: error?.message || 'Please try again later.',
      });
    } finally {
      setRemovingUuid(null);
    }
  };

  if (loading) {
    return (
      <div className="card border border-gray-200 shadow-sm">
        <div className="card-body flex items-center justify-center gap-3 p-8 text-sm text-gray-600">
          <Loader2 className="size-4 animate-spin" />
          Loading seats...
        </div>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const cycleLabel = summary.billing_cycle === 'year' ? 'year' : 'month';
  const quote = summary.next_seat_quote;
  const renewsAt = formatDate(summary.renews_at);

  return (
    <div className="card border border-gray-200 shadow-sm">
      <div className="card-body p-0">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-light p-6">
          <div className="flex items-start gap-4">
            <span className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary-light text-primary">
              <Users className="size-6" />
            </span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Seats</h3>
              <p className="mt-1 text-sm text-gray-600">
                {summary.seat_count} {summary.seat_count === 1 ? 'seat' : 'seats'} ·{' '}
                {formatCurrency(summary.seat_price_for_cycle)}/user/{cycleLabel}
                {renewsAt ? ` · renews ${renewsAt}` : ''}
              </p>
            </div>
          </div>

          <div className="text-right">
            <div className="text-xs uppercase tracking-wide text-gray-500">Recurring total</div>
            <div className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.recurring_total)}
              <span className="ml-1 text-xs font-normal text-gray-600">/{cycleLabel}</span>
            </div>
            {summary.credit_balance > 0 && (
              <div className="mt-1 text-xs font-medium text-success">
                {formatCurrency(summary.credit_balance)} credit applied at renewal
              </div>
            )}
          </div>
        </div>

        <div className="grid gap-4 p-6 md:grid-cols-2">
          <div className="rounded-xl border border-dashed border-gray-300 bg-light-active p-4 dark:bg-coal-100">
            <div className="text-sm font-medium text-gray-900">Add a seat</div>
            {quote ? (
              <p className="mt-1 text-xs leading-5 text-gray-600">
                {formatCurrency(quote.total_amount)} today for the {quote.remaining_days} days left in this
                cycle, then {formatCurrency(summary.seat_price_for_cycle)} per {cycleLabel}.
              </p>
            ) : (
              <p className="mt-1 flex items-start gap-1.5 text-xs leading-5 text-warning">
                <AlertCircle className="mt-px size-3.5 shrink-0" />
                Your subscription needs renewing before seats can be added.
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm mt-4 justify-center"
              onClick={() => navigate('/account/members/users')}
              disabled={!quote}
            >
              <UserPlus className="size-4" />
              Add User
            </button>
          </div>

          <div className="rounded-xl border border-dashed border-gray-300 bg-light-active p-4 dark:bg-coal-100">
            <div className="text-sm font-medium text-gray-900">Remove a seat</div>
            <p className="mt-1 text-xs leading-5 text-gray-600">
              Disabling someone frees their seat. Unused time comes back as credit against your next
              renewal — it is not refunded to the card.
            </p>
            <button
              type="button"
              className="btn btn-light btn-sm mt-4 justify-center"
              onClick={() => setShowOccupants((open) => !open)}
            >
              <Minus className="size-4" />
              {showOccupants ? 'Hide seats' : 'Manage seats'}
            </button>
          </div>
        </div>

        {showOccupants && (
          <div className="border-t border-light">
            {summary.occupants?.length ? (
              <div className="divide-y divide-light">
                {summary.occupants.map((occupant) => (
                  <div
                    key={occupant.subscription_uuid}
                    className="flex flex-wrap items-center justify-between gap-3 px-6 py-3"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-gray-900">
                        {occupant.name || occupant.email}
                      </div>
                      <div className="truncate text-xs text-gray-600">{occupant.email}</div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-xs text-gray-500">Credit if removed</div>
                        <div className="text-sm font-semibold text-gray-900">
                          {formatCurrency(occupant.credit_if_removed)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-light btn-outline"
                        onClick={() => handleRemoveSeat(occupant)}
                        disabled={removingUuid === occupant.user_uuid}
                      >
                        {removingUuid === occupant.user_uuid ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          'Remove'
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-sm text-gray-600">No seats on this subscription yet.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { SeatsCard };
